import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject,
  OnInit,
  OnDestroy,
  DOCUMENT,
  signal,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { FollowStateService } from '../../../services/follow-state.service';

interface User {
  id: number;
  name: string;
  slug: string;
  follow_stats: {
    followed_by_current_user: boolean;
    follower_count: number;
  };
  current_user_permissions?: {
    follow?: boolean;
  };
}

@Component({
  selector: 'ut-community-follow-btn',
  templateUrl: './community-follow-btn.component.html',
  styleUrls: ['./community-follow-btn.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityFollowBtnComponent implements OnInit, OnDestroy {
  @Input() user!: User;
  // 'legacy' keeps the checkbox toggle the AngularJS pages use; '2026' renders a ut-btn.
  @Input() design: 'legacy' | '2026' = 'legacy';
  @Input() size: 'md' | 'sm' = 'md';
  // The new state on each toggle, so a page can keep its own counts in step (the profile's "Following").
  @Output() followChange = new EventEmitter<boolean>();
  // 2026 design: no "Unfollow" preview until the pointer leaves after a toggle.
  readonly hoverLocked = signal(false);

  private uniqueId: string = `follow-${Math.random().toString(36).substring(2, 9)}`;
  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private followStateService: FollowStateService,
    @Inject('UserService') private UserService: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    @Inject('$analytics') private $analytics: any,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit(): void {
    // Subscribe to follow state changes for this user
    this.followStateService
      .getFollowState$(this.user.id)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => state !== undefined),
      )
      .subscribe((state) => {
        // Update local user object when state changes from other components
        if (state) {
          this.user.follow_stats.followed_by_current_user = state.isFollowing;
          // People-list rows have no follower_count, so a broadcast from one of them carries none.
          if (Number.isFinite(state.followerCount)) this.user.follow_stats.follower_count = state.followerCount;
          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get followCheckboxId(): string {
    return this.uniqueId;
  }

  get ariaLabel(): string {
    const action = this.user.follow_stats.followed_by_current_user ? 'Following' : 'Follow';
    return `${action} ${this.user.name}`;
  }

  get isFollowing(): boolean {
    return this.user.follow_stats.followed_by_current_user;
  }

  set isFollowing(value: boolean) {
    this.user.follow_stats.followed_by_current_user = value;
  }

  private updateCurrentUserPermissions(followResponse: any): any {
    if (followResponse) {
      this.user.current_user_permissions = followResponse.data.current_user_permissions;
    }
    this.cdr.markForCheck();
    return followResponse;
  }

  private trackFollowing(action: 'follow' | 'unfollow'): void {
    this.$analytics.eventTrack(action, {
      category: 'Follows',
      label: this.document.title,
    });
  }

  private broadcastFollowState(): void {
    // Broadcast the new follow state to other components
    this.followStateService.updateFollowState(
      this.user.id,
      this.user.follow_stats.followed_by_current_user,
      this.user.follow_stats.follower_count,
    );
  }

  // Only adjusts a count the API sent; Followers / Following rows don't include one.
  private adjustFollowerCount(delta: number): void {
    const count = this.user.follow_stats.follower_count;
    if (Number.isFinite(count)) this.user.follow_stats.follower_count = Math.max(0, count + delta);
  }

  toggleFollow(): void {
    if (this.user.follow_stats.followed_by_current_user) {
      this.UserService.follow(this.user.id).then((response: any) => {
        this.updateCurrentUserPermissions(response);
        this.broadcastFollowState();
      });
      this.adjustFollowerCount(1);
      this.trackFollowing('follow');
    } else {
      this.UserService.unfollow(this.user.id).then((response: any) => {
        this.updateCurrentUserPermissions(response);
        this.broadcastFollowState();
      });
      this.adjustFollowerCount(-1);
      this.trackFollowing('unfollow');
    }
    this.followChange.emit(this.user.follow_stats.followed_by_current_user);
    this.cdr.markForCheck();
  }

  followClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.LightTesterErrorService.show()) {
      return;
    }

    this.user.follow_stats.followed_by_current_user = !this.user.follow_stats.followed_by_current_user;
    this.hoverLocked.set(true);
    this.toggleFollow();
  }

  followKeydown(event: KeyboardEvent): void {
    // Handle Enter (13) and Space (32) keys
    if (event.keyCode === 13 || event.keyCode === 32) {
      event.preventDefault();

      if (this.LightTesterErrorService.show()) {
        return;
      }

      this.user.follow_stats.followed_by_current_user = !this.user.follow_stats.followed_by_current_user;
      this.toggleFollow();
    }
  }
}
